import { Dialog, DialogContent } from '@/shared/widgets/dialog';
import { Sheet, SheetContent } from '@/shared/widgets/sheet';
import { useIsMobile } from '@/core/hooks/useMediaQuery';
import { PopupCard } from '@/shared/components/PopupCard';
import { Input } from '@/shared/widgets/input';

export interface PincodePopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inputValue: string;
  error?: string | null;
  onChange: (value: string) => void;
  onSubmit: () => void | Promise<void>;
  description?: string;
}

export interface PincodePopupContentProps {
  inputValue: string;
  error?: string | null;
  onChange: (value: string) => void;
  onSubmit: () => void | Promise<void>;
  onClose: () => void;
  description?: string;
}

const DEFAULT_PINCODE_DESCRIPTION =
  'Find the closest store for browsing our latest designs. Order your desired jewellery online and collect it from the store.';

export function PincodePopupContent({
  inputValue,
  error,
  onChange,
  onSubmit,
  onClose,
  description = DEFAULT_PINCODE_DESCRIPTION,
}: PincodePopupContentProps) {
  return (
    <PopupCard
      title="Check Availability In Your Pincode"
      onClose={onClose}
      headerBgClassName="text-base text-white"
      titleClassName="text-base"
      titleBgColor="--plp-top-bar-bg"
    >
      <p className="text-center text-[14px] leading-relaxed text-gray-600">
        {description}
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
        className="space-y-4"
      >
        <div className="flex flex-col gap-2">
          <div className="flex overflow-hidden rounded-md border border-gray-300 bg-white">
            <Input
              id="plp-pincode-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={inputValue}
              onChange={(event) => onChange(event.target.value)}
              className="flex-1 px-3 py-2 text-sm outline-none"
              placeholder="Pincode"
            />
            <button
              type="submit"
              className="bg-[#f45b4d] px-5 py-2 text-sm font-semibold uppercase tracking-wide text-white hover:bg-[#e14c3e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f45b4d] focus-visible:ring-offset-2"
            >
              Update
            </button>
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      </form>
    </PopupCard>
  );
}

export function PincodePopup({
  open,
  onOpenChange,
  inputValue,
  error,
  onChange,
  onSubmit,
  description,
}: PincodePopupProps) {
  const isMobile = useIsMobile();

  const handleClose = () => {
    onOpenChange(false);
  };

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          showCloseButton={false}
          className="h-auto max-h-[90vh] overflow-y-auto p-0 gap-0"
        >
          <PincodePopupContent
            inputValue={inputValue}
            error={error}
            onChange={onChange}
            onSubmit={onSubmit}
            onClose={handleClose}
            description={description}
          />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="w-[90vw] max-w-lg overflow-hidden p-0 gap-0"
      >
        <PincodePopupContent
          inputValue={inputValue}
          error={error}
          onChange={onChange}
          onSubmit={onSubmit}
          onClose={handleClose}
          description={description}
        />
      </DialogContent>
    </Dialog>
  );
}

